import React from "react";
import ParagraphTag from "../../reusebale/Paragraph";
import { FileText, Hourglass, CheckCircle, XCircle } from "lucide-react";
import MyLoader from "../../reusebale/myLoader";
import db from "../../../db/index.json";

const TransactionsList = ({ 
  transactions, 
  loading, 
  leaseDetails,
  limit = null,
  showHeaders = true,
  showExtendedColumns = false
}) => {
  const { rightPanel } = db;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
    const timePart = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${datePart}, ${timePart}`;
  };

  const formatCurrency = (amount) => {
    if (!amount) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Get property name from leaseDetails
  const getPropertyName = () => {
    if (!leaseDetails) return "N/A";
    const propertyName = leaseDetails.propertyInfo?.propertyName 
      || leaseDetails.propertyInfo?.completeAddress 
      || leaseDetails.propertyInfo?.address 
      || "N/A";
    return propertyName;
  };

  // Determine if transaction is debit or credit (TENANT perspective - INVERTED)
  // For tenant: External → Internal = DEBIT (money leaving tenant), Internal → External = CREDIT (money coming to tenant)
  const getTransactionType = (transaction) => {
    const isDebit = transaction.sourceAccountType === "external" && transaction.destinationAccountType === "internal";
    const isCredit = transaction.sourceAccountType === "internal" && transaction.destinationAccountType === "external";
    return { isDebit, isCredit };
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || "";
    
    // Green statuses: COMPLETED, SUCCESS, COMPLETE, REFUND
    if (statusLower === "completed" || statusLower === "success" || statusLower === "complete" || statusLower === "refund") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          {status?.toUpperCase() || "UNKNOWN"}
        </span>
      );
    } 
    // Yellow statuses: PENDING, SCHEDULED
    else if (statusLower === "pending" || statusLower === "scheduled") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
          {status?.toUpperCase() || "SCHEDULED"}
        </span>
      );
    } 
    // Blue status: NEW
    else if (statusLower === "new") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {status?.toUpperCase()}
        </span>
      );
    } 
    // Grey status: PROCESSING
    else if (statusLower === "processing") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
          <Hourglass className="w-3 h-3 mr-1" />
          {status?.toUpperCase()}
        </span>
      );
    } 
    // Red statuses: FAILED, REJECTED
    else if (statusLower === "failed" || statusLower === "rejected") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1" />
          {status?.toUpperCase() || "FAILED"}
        </span>
      );
    }
    
    // Default: blue (same as NEW) for unknown statuses
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        {status?.toUpperCase() || "UNKNOWN"}
      </span>
    );
  };

  const propertyName = getPropertyName();

  // Format Invoice ID
  const formatInvoiceId = (transactionId) => {
    if (!transactionId) return "N/A";
    return `INV-${transactionId.slice(-8)}`;
  };

  // Calculate column widths based on whether extended columns are shown
  const getColumnWidths = () => {
    if (showExtendedColumns) {
      return {
        invoiceId: "w-[15%]",
        tenants: "w-[15%]",
        date: "w-[10%]",
        amount: "w-[8%]",
        securityDeposit: "w-[8%]",
        total: "w-[8%]",
        status: "w-[8%]",
        purpose: "w-[10%]",
        quick: "w-[18%]"
      };
    } else {
      return {
        invoiceId: "w-[20%]",
        tenants: "w-[23%]",
        date: "w-[14%]",
        amount: "w-[11%]",
        securityDeposit: "w-[12%]",
        total: "w-[11%]",
        quick: "w-[9%]"
      };
    }
  };

  const columnWidths = getColumnWidths();

  // Apply limit if specified
  const displayTransactions = limit 
    ? transactions.slice(0, limit)
    : transactions;

  if (!loading && transactions.length === 0) {
    return (
      <div className="text-center py-8">
        <ParagraphTag
          content="No transactions to display"
          classes="text-darkColor/40 font-popinsRegular text-sm"
        />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex justify-center items-center z-10 rounded-lg min-h-[200px]">
          <MyLoader />
        </div>
      )}
      
      {/* Content */}
      <div className={loading ? "pointer-events-none" : ""}>
      {showHeaders && (
        <div className="flex border-b-[1px] border-darkColor/40 pb-1 mt-6">
          <div className={columnWidths.invoiceId}>
            <ParagraphTag
              content="Invoice ID"
              classes="text-darkColor/40 font-popinsMedium text-sm"
            />
          </div>
          <div className={`${columnWidths.tenants} pl-3`}>
            <ParagraphTag
              content="Property"
              classes="text-darkColor/40 font-popinsMedium text-sm"
            />
          </div>
          <div className={`${columnWidths.date} pl-3`}>
            <ParagraphTag
              content="Date"
              classes="text-darkColor/40 font-popinsMedium text-sm"
            />
          </div>
          <div className={`${columnWidths.amount} pl-3`}>
            <ParagraphTag
              content="Amount"
              classes="text-darkColor/40 font-popinsMedium text-sm"
            />
          </div>
          <div className={`${columnWidths.securityDeposit} pl-3`}>
            <ParagraphTag
              content="Security Deposit"
              classes="text-darkColor/40 font-popinsMedium text-sm"
            />
          </div>
          <div className={`${columnWidths.total} pl-3`}>
            <ParagraphTag
              content="Total"
              classes="text-darkColor/40 font-popinsMedium text-sm"
            />
          </div>
          {showExtendedColumns && (
            <>
              <div className={`${columnWidths.status} pl-3`}>
                <ParagraphTag
                  content="Status"
                  classes="text-darkColor/40 font-popinsMedium text-sm"
                />
              </div>
              <div className={`${columnWidths.purpose} pl-3`}>
                <ParagraphTag
                  content="Purpose"
                  classes="text-darkColor/40 font-popinsMedium text-sm"
                />
              </div>
            </>
          )}
          <div className={`${columnWidths.quick} text-end`}>
            <ParagraphTag
              content={rightPanel.quick}
              classes="text-darkColor/40 font-popinsMedium text-sm"
            />
          </div>
        </div>
      )}
      <div className={showHeaders && limit ? "h-[220px] overflow-y-scroll panelScroll" : ""}>
        {displayTransactions.map((transaction, index) => {
          const { isDebit, isCredit } = getTransactionType(transaction);
          const amountColor = isDebit ? "text-red-600" : isCredit ? "text-green-600" : "text-darkColor";
          const typeText = isDebit ? "Debit" : isCredit ? "Credit" : "N/A";
          const typeColor = isDebit ? "text-red-600" : isCredit ? "text-green-600" : "text-darkColor/60";
          
          return (
            <div 
              key={transaction._id || index} 
              className="flex items-center bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-3 mb-2 hover:shadow-md transition-shadow"
            >
              <div className={`${columnWidths.invoiceId} flex items-center`}>
                <div className="w-7 h-7 flex-shrink-0 mr-2">
                  <div className="w-full h-full bg-blue-100 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <ParagraphTag
                  content={formatInvoiceId(transaction._id)}
                  classes="text-darkColor font-popinsSemiBold text-sm"
                />
              </div>
              <div className={`${columnWidths.tenants} flex items-center pl-3`}>
                <div className="flex-1">
                  <ParagraphTag
                    content={propertyName}
                    classes="text-darkColor font-popinsSemiBold text-sm"
                  />
                </div>
              </div>
              <div className={`${columnWidths.date} flex items-center pl-3`}>
                <ParagraphTag
                  content={formatDate(transaction.createdAt)}
                  classes="text-darkColor font-popinsMedium text-sm"
                />
              </div>
              <div className={`${columnWidths.amount} flex items-center pl-3`}>
                <ParagraphTag
                  content={formatCurrency(transaction.amount - (transaction.security_deposit || 0))}
                  classes="text-darkColor font-popinsMedium text-sm"
                />
              </div>
              <div className={`${columnWidths.securityDeposit} flex items-center pl-3`}>
                <ParagraphTag
                  content={(transaction.security_deposit && transaction.security_deposit > 0) 
                    ? formatCurrency(transaction.security_deposit) 
                    : "N/A"}
                  classes="text-darkColor font-popinsMedium text-sm"
                />
              </div>
              <div className={`${columnWidths.total} flex items-center pl-3`}>
                <div className="flex items-center gap-1">
                  <ParagraphTag
                    content={formatCurrency(transaction.amount)}
                    classes={`${amountColor} font-popinsSemiBold text-sm`}
                  />
                  <span className={`${amountColor} font-popinsMedium text-xs`}>
                    {isDebit ? "DR" : isCredit ? "CR" : ""}
                  </span>
                </div>
              </div>
              {showExtendedColumns && (
                <>
                  <div className={`${columnWidths.status} flex items-center pl-3`}>
                    {getStatusBadge(transaction.status)}
                  </div>
                  <div className={`${columnWidths.purpose} flex items-center pl-3`}>
                    <ParagraphTag
                      content={transaction.purpose || "N/A"}
                      classes="text-darkColor font-popinsMedium text-sm"
                    />
                  </div>
                </>
              )}
              <div className={`${columnWidths.quick} flex items-center justify-end`}>
                <div
                  className="flex justify-center items-center bg-themeColor w-8 h-8 rounded-lg cursor-pointer"
                  title="View Invoice"
                >
                  <FileText color="#FFFFFF" size={16} strokeWidth={2} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
};

export default TransactionsList;

